import {
  resolveCountdownDisplay,
  applyCountdownBarShellToSection,
  COUNTDOWN_DISPLAY_LAYOUT,
} from "./countdownDisplay";
import {
  POPUP_LAYOUT_IDS,
  getPopupLayoutConfig,
  isPopupLayout,
  resolvePopupLayoutId,
} from "../popup-layouts/index.js";
import {
  isSectionVisible,
  resolveSectionVisibleDisplay,
  setSectionVisibility,
} from "../../shared/section-visibility.js";
import {
  resolveCatalogCtaCssPatch,
  resolvePopupThemeTokens,
} from "./resolve-popup-theme-tokens.js";
import { ensureAiRecommendationUpsellCatalogCtaButtons } from "../../shared/ai-recommendation-upsell-catalog-cta.js";
import {
  AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT,
  applyAiRecommendationUpsellBannerDefaults,
  normalizeAiRecommendationUpsellLayoutOptions,
  normalizeAiRecommendationUpsellSections,
} from "../../shared/ai-recommendation-upsell-schema.js";

const AI_RECOMMENDATION_UPSELL_LAYOUT_ID =
  POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL;

const LEGACY_COUNTDOWN_BAR_DEFAULTS = {
  urgencyText: "Only 3 left in stock — offer expires in",
  showUrgencyText: true,
  showIcon: true,
  iconEmoji: "⚡",
  backgroundColor: "#c0392b",
  textColor: "#ffffff",
  textFontSize: "14px",
  textFontWeight: "600",
};

const SHELL_DESKTOP_WIDTH = 625;
const LEGACY_SHELL_DESKTOP_WIDTH = 480;
const SHELL_WIDTH_SCALE = SHELL_DESKTOP_WIDTH / LEGACY_SHELL_DESKTOP_WIDTH;

const scaleDesktopBox = (box) => ({
  x: box.x,
  y: box.y,
  width: Math.round(box.width * SHELL_WIDTH_SCALE),
  height: box.height,
});

const TOP_LAYOUT = {
  desktop: {
    countdown: { x: 0, y: 0, width: SHELL_DESKTOP_WIDTH, height: 48 },
    aiBanner: { x: 0, y: 48, width: SHELL_DESKTOP_WIDTH, height: 76 },
    product: scaleDesktopBox({ x: 12, y: 128, width: 456, height: 368 }),
    socialProof: scaleDesktopBox({ x: 12, y: 504, width: 456, height: 40 }),
    primaryCta: scaleDesktopBox({ x: 12, y: 552, width: 456, height: 48 }),
    secondaryCta: scaleDesktopBox({ x: 12, y: 608, width: 456, height: 44 }),
    trust: scaleDesktopBox({ x: 12, y: 660, width: 456, height: 36 }),
    footer: scaleDesktopBox({ x: 12, y: 700, width: 456, height: 28 }),
  },
  mobile: {
    countdown: { x: 0, y: 0, width: 320, height: 44 },
    aiBanner: { x: 0, y: 44, width: 320, height: 72 },
    product: { x: 8, y: 120, width: 304, height: 340 },
    socialProof: { x: 8, y: 468, width: 304, height: 36 },
    primaryCta: { x: 8, y: 512, width: 304, height: 44 },
    secondaryCta: { x: 8, y: 564, width: 304, height: 40 },
    trust: { x: 8, y: 612, width: 304, height: 32 },
    footer: { x: 8, y: 648, width: 304, height: 24 },
  },
};

const PRODUCT_SUB_LAYOUT = {
  desktop: {
    COVER_IMAGE: scaleDesktopBox({ x: 12, y: 8, width: 126, height: 126 }),
    TITLE: scaleDesktopBox({ x: 150, y: 8, width: 294, height: 42 }),
    DESCRIPTION: scaleDesktopBox({ x: 150, y: 54, width: 294, height: 76 }),
    PRICE: scaleDesktopBox({ x: 12, y: 136, width: 432, height: 52 }),
    VARIANT_PICKER: scaleDesktopBox({ x: 12, y: 196, width: 432, height: 40 }),
    QUANTITY_PICKER: scaleDesktopBox({ x: 12, y: 242, width: 210, height: 40 }),
  },
  mobile: {
    COVER_IMAGE: { x: 8, y: 8, width: 96, height: 96 },
    TITLE: { x: 112, y: 8, width: 192, height: 34 },
    DESCRIPTION: { x: 112, y: 46, width: 192, height: 56 },
    PRICE: { x: 8, y: 108, width: 296, height: 46 },
    VARIANT_PICKER: { x: 8, y: 160, width: 296, height: 38 },
    QUANTITY_PICKER: { x: 8, y: 204, width: 170, height: 34 },
  },
};

const getRoleBoxesFromLayoutTree = (layoutTree) => {
  const fallback = TOP_LAYOUT;
  if (!layoutTree || resolvePopupLayoutId({ layoutPreset: layoutTree?.preset }) !== AI_RECOMMENDATION_UPSELL_LAYOUT_ID) return fallback;
  const boxes = layoutTree?.generatedBoxes;
  if (
    boxes?.desktop &&
    boxes?.mobile &&
    typeof boxes.desktop === "object" &&
    typeof boxes.mobile === "object"
  ) {
    return {
      desktop: { ...fallback.desktop, ...boxes.desktop },
      mobile: { ...fallback.mobile, ...boxes.mobile },
    };
  }
  return fallback;
};

const ensureSetting = (section) => {
  section.setting = section.setting || {};
  section.setting.position = section.setting.position || {};
  section.setting.position.displacement =
    section.setting.position.displacement || {};
  section.setting.dimension = section.setting.dimension || {};
};

const hasPosition = (section, view) => {
  const pos = section?.setting?.position?.displacement?.[view];
  const dim = section?.setting?.dimension?.[view];
  return (
    typeof pos?.x === "number" &&
    typeof pos?.y === "number" &&
    dim != null &&
    typeof dim.width !== "undefined" &&
    typeof dim.height !== "undefined"
  );
};

const applyLayout = (section, desktopBox, mobileBox, force = false) => {
  if (!section || !desktopBox || !mobileBox) return;
  ensureSetting(section);

  if (force || !hasPosition(section, "desktop")) {
    section.setting.position.displacement.desktop = {
      x: desktopBox.x,
      y: desktopBox.y,
    };
    section.setting.dimension.desktop = {
      width: desktopBox.width,
      height: desktopBox.height,
    };
  }

  if (force || !hasPosition(section, "mobile")) {
    section.setting.position.displacement.mobile = {
      x: mobileBox.x,
      y: mobileBox.y,
    };
    section.setting.dimension.mobile = {
      width: mobileBox.width,
      height: mobileBox.height,
    };
  }
};

const getRoleSection = (sections, role) =>
  sections.find((s) => s?.type === "TEXT" && s?.setting?.role === role);

const getByType = (sections, type) => sections.find((s) => s?.type === type);

const getLayoutOptions = (gs = {}) => {
  const layoutConfig = getPopupLayoutConfig(gs) || gs?.layoutTree || {};
  const options = layoutConfig?.options || {};
  const variants = options?.designVariants || {};
  const normalizedCountdown = (() => {
    const raw = variants.countdown || "countdown-v1";
    if (raw === "design-1") return "countdown-v1";
    if (raw === "design-2") return "countdown-v2";
    if (raw === "design-3") return "countdown-v3";
    return raw;
  })();
  const normalizedVariantPicker = (() => {
    const raw = variants.variantPicker || "tabs-v1";
    if (raw === "design-1") return "tabs-v1";
    if (raw === "design-2") return "dropdown-v1";
    return raw;
  })();

  return {
    borderRadius: Number(options.borderRadius) || 20,
    showAiSummaryRibbon: options.showAiSummaryRibbon ?? true,
    showSocialProof: options.showSocialProof ?? true,
    showVariantPicker: options.showVariantPicker ?? true,
    showQuantityAndVolume: options.showQuantityAndVolume ?? true,
    showTrustBadges: options.showTrustBadges ?? true,
    buttonText: options.buttonText || AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT,
    declineText: options.declineText || "No thanks, I'll pay full price",
    countdownBar: {
      ...LEGACY_COUNTDOWN_BAR_DEFAULTS,
      ...(options.countdownBar || {}),
    },
    designVariants: {
      countdown: normalizedCountdown,
      aiSummaryRibbon: variants.aiSummaryRibbon || "design-1",
      productCard: variants.productCard || "design-1",
      variantPicker: normalizedVariantPicker,
      ctaButtons: variants.ctaButtons || "design-1",
      socialProof: variants.socialProof || "design-1",
      trustBadges: variants.trustBadges || "design-1",
    },
  };
};

/** Respect editor eye-toggle (`display: none`) when layout options want a section shown. */
const setVisibility = (section, layoutWantsVisible) => {
  if (!section) return;
  if (layoutWantsVisible && !isSectionVisible(section)) return;
  const visibleDisplay = layoutWantsVisible
    ? resolveSectionVisibleDisplay(section, "flex")
    : "none";
  setSectionVisibility(section, layoutWantsVisible, visibleDisplay);
};

const markLayoutFlow = (section) => {
  if (!section) return;
  section.setting = section.setting || {};
  section.setting.layoutFlow = true;
};

const ensureButtonReadable = (button, primary, themeTokens) => {
  if (!button) return;
  ensureSetting(button);
  button.setting.customCSS = button.setting.customCSS || {};
  const themed = resolveCatalogCtaCssPatch(button, themeTokens, { primary });
  const desktop = button.setting.customCSS.desktop || {};
  const mobile = button.setting.customCSS.mobile || {};
  const hiddenDesktop = desktop.display === "none";
  const hiddenMobile = mobile.display === "none";

  button.setting.customCSS.desktop = {
    ...desktop,
    ...themed.desktop,
    ...(hiddenDesktop ? { display: "none" } : { display: desktop.display || "flex" }),
    borderStyle: desktop.borderStyle || "solid",
    borderWidth: desktop.borderWidth ?? 1,
    fontSize: desktop.fontSize || (primary ? "16px" : "14px"),
    fontWeight: desktop.fontWeight || "700",
  };
  button.setting.customCSS.mobile = {
    ...mobile,
    ...themed.mobile,
    ...(hiddenMobile ? { display: "none" } : { display: mobile.display || "flex" }),
    borderStyle: mobile.borderStyle || desktop.borderStyle || "solid",
    borderWidth: mobile.borderWidth ?? desktop.borderWidth ?? 1,
    fontSize: mobile.fontSize || (primary ? "15px" : "13px"),
    fontWeight: mobile.fontWeight || desktop.fontWeight || "700",
  };
};

const ensureProductFallback = (product) => {
  if (!product) return;
  if (Array.isArray(product.products) && product.products.length > 0) return;
  product.products = [
    {
      id: "ai-upsell-preview-product",
      title: "Premium Silk Eye Mask Pro",
      body_html:
        "Blocks 100% light · Memory foam contour · Adjustable strap · Clinically shown to improve sleep quality",
      images: [
        {
          src: "https://cdn.shopify.com/s/files/1/0638/0401/4723/files/Women_sShirt.jpg?v=1740553920",
        },
      ],
      variants: [
        {
          id: "v1",
          title: "Midnight Black",
          price: "24.00",
          compare_at_price: "34.00",
        },
        {
          id: "v2",
          title: "Blush Pink",
          price: "24.00",
          compare_at_price: "34.00",
        },
      ],
    },
  ];
};

const syncCountdownSectionDisplay = (section, popup, layoutOptions) => {
  if (!section) return null;
  const display = resolveCountdownDisplay(section, popup);
  section.setting = section.setting || {};
  if (!section.setting.display?.layout) {
    section.setting.display = {
      ...display,
      layout: COUNTDOWN_DISPLAY_LAYOUT.BAR,
      urgencyText:
        display.urgencyText ||
        layoutOptions.countdownBar?.urgencyText ||
        LEGACY_COUNTDOWN_BAR_DEFAULTS.urgencyText,
    };
  }
  const resolved = resolveCountdownDisplay(section, popup);
  applyCountdownBarShellToSection(section, resolved);
  return resolved;
};

const clearAiBannerSurface = (section) => {
  applyAiRecommendationUpsellBannerDefaults(section);
};

const ensureAiBannerSampleCopy = (section) => {
  applyAiRecommendationUpsellBannerDefaults(section);
};

const hasMeaningfulText = (value) => {
  const raw = String(value ?? "").trim();
  return Boolean(raw && raw !== "<p></p>");
};

const resolveSectionText = (section, fallback) =>
  hasMeaningfulText(section?.text) ? section.text : fallback;

const applyAiSummaryVariant = (_section, _variant) => {
  // Pitch layout typography + visibility come from schema customCSS; do not re-merge on every preview sync.
};

const applyProductVariant = (product, variant) => {
  if (!product || !Array.isArray(product.sections)) return;
  const byType = (t) => product.sections.find((s) => s.type === t);
  const cover = byType("COVER_IMAGE");
  const title = byType("TITLE");
  const description = byType("DESCRIPTION");
  const price = byType("PRICE");
  if (variant === "design-2") {
    if (cover?.setting?.customCSS?.desktop) cover.setting.customCSS.desktop.borderRadius = 18;
    if (cover?.setting?.customCSS?.mobile) cover.setting.customCSS.mobile.borderRadius = 14;
    if (title?.setting?.customCSS?.desktop) title.setting.customCSS.desktop.fontSize = "22px";
    if (description?.setting?.customCSS?.desktop) description.setting.customCSS.desktop.fontSize = "14px";
    if (price?.setting?.customCSS?.desktop) {
      price.setting.customCSS.desktop.backgroundColor = "#f8fafc";
      price.setting.customCSS.desktop.borderRadius = 12;
      price.setting.customCSS.desktop.padding = "8px 12px";
    }
    return;
  }
  if (variant === "design-3") {
    if (cover?.setting?.customCSS?.desktop) cover.setting.customCSS.desktop.borderRadius = 10;
    if (cover?.setting?.customCSS?.mobile) cover.setting.customCSS.mobile.borderRadius = 10;
    if (title?.setting?.customCSS?.desktop) {
      title.setting.customCSS.desktop.fontSize = "19px";
      title.setting.customCSS.desktop.fontWeight = "600";
    }
    if (description?.setting?.customCSS?.desktop) description.setting.customCSS.desktop.color = "#64748b";
  }
};

const applyVariantPickerVariant = (section, variant) => {
  if (!section) return;
  section.setting = section.setting || {};
  section.setting.variantPickerVariant = variant || "tabs-v1";
  section.setting.customCSS = section.setting.customCSS || {};
  if (variant === "dropdown-v1") {
    section.setting.customCSS.desktop = {
      borderRadius: "12px",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#d1d5db",
      backgroundColor: "#ffffff",
      color: "#334155",
      padding: "0px 12px",
      ...(section.setting.customCSS.desktop || {}),
    };
    section.setting.customCSS.mobile = {
      borderRadius: "10px",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#d1d5db",
      backgroundColor: "#ffffff",
      color: "#334155",
      padding: "0px 10px",
      ...(section.setting.customCSS.mobile || {}),
    };
    return;
  }
  section.setting.customCSS.desktop = {
    backgroundColor: "transparent",
    borderStyle: "none",
    borderWidth: 0,
    padding: "0px",
    ...(section.setting.customCSS.desktop || {}),
  };
  section.setting.customCSS.mobile = {
    backgroundColor: "transparent",
    borderStyle: "none",
    borderWidth: 0,
    padding: "0px",
    ...(section.setting.customCSS.mobile || {}),
  };
};

const applyCtaVariant = (button, variant, primary, themeTokens) => {
  if (!button) return;
  ensureButtonReadable(button, primary, themeTokens);
  if (variant === "design-2") {
    button.setting.customCSS.desktop = {
      ...(button.setting.customCSS.desktop || {}),
      borderRadius: 999,
      letterSpacing: "0.01em",
    };
    button.setting.customCSS.mobile = {
      ...(button.setting.customCSS.mobile || {}),
      borderRadius: 999,
    };
    return;
  }
  if (variant === "design-3" && primary) {
    const desktop = button.setting.customCSS.desktop || {};
    const mobile = button.setting.customCSS.mobile || {};
    button.setting.customCSS.desktop = {
      ...desktop,
      backgroundColor: desktop.backgroundColor ?? themeTokens.primary,
      borderColor: desktop.borderColor ?? themeTokens.primary,
      color: desktop.color ?? themeTokens.primaryText,
    };
    button.setting.customCSS.mobile = {
      ...mobile,
      backgroundColor: mobile.backgroundColor ?? desktop.backgroundColor ?? themeTokens.primary,
      borderColor: mobile.borderColor ?? desktop.borderColor ?? themeTokens.primary,
      color: mobile.color ?? desktop.color ?? themeTokens.primaryText,
    };
  }
};

const applySocialProofVariant = (section, variant) => {
  if (!section) return;
  section.setting.customCSS = section.setting.customCSS || {};
  if (variant === "design-2") {
    section.setting.customCSS.desktop = {
      ...(section.setting.customCSS.desktop || {}),
      backgroundColor: "#ecfdf5",
      color: "#065f46",
      borderRadius: "999px",
    };
    section.setting.customCSS.mobile = {
      ...(section.setting.customCSS.mobile || {}),
      backgroundColor: "#ecfdf5",
      color: "#065f46",
      borderRadius: "999px",
    };
    return;
  }
  if (variant === "design-3") {
    section.setting.customCSS.desktop = {
      ...(section.setting.customCSS.desktop || {}),
      backgroundColor: "#f8fafc",
      color: "#475569",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#e2e8f0",
    };
    section.setting.customCSS.mobile = {
      ...(section.setting.customCSS.mobile || {}),
      backgroundColor: "#f8fafc",
      color: "#475569",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#e2e8f0",
    };
  }
};

const applyTrustVariant = (section, variant) => {
  if (!section || variant !== "design-3") return;
  section.setting.customCSS = section.setting.customCSS || {};
  section.setting.customCSS.desktop = {
    ...(section.setting.customCSS.desktop || {}),
    color: "#64748b",
    fontSize: "11px",
  };
  section.setting.customCSS.mobile = {
    ...(section.setting.customCSS.mobile || {}),
    color: "#64748b",
    fontSize: "10px",
  };
};

export const ensureAiRecommendationUpsellLayoutCompatibility = (popup) => {
  if (!popup || !Array.isArray(popup?.schema)) return popup;

  const gs = popup?.generalSettings || {};
  if (!isPopupLayout(popup, AI_RECOMMENDATION_UPSELL_LAYOUT_ID)) return popup;
  normalizeAiRecommendationUpsellLayoutOptions(gs);
  const themeTokens = resolvePopupThemeTokens(popup, { activeStep: 0, isMobile: false });
  const options = getLayoutOptions(gs);
  const forceCatalogLayout = gs.lockDraggable !== false;
  const placeLayout = (section, desktopBox, mobileBox) =>
    applyLayout(section, desktopBox, mobileBox, forceCatalogLayout);

  const step0 = popup.schema?.[0];
  const sections = step0?.section || step0?.sections;
  if (!Array.isArray(sections) || sections.length === 0) return popup;

  for (let i = sections.length - 1; i >= 0; i -= 1) {
    const section = sections[i];
    if (section?.type === "TEXT" && section?.setting?.role === "urgency") {
      sections.splice(i, 1);
    }
  }

  const roleBoxes = getRoleBoxesFromLayoutTree(getPopupLayoutConfig(gs));

  const countdown = getByType(sections, "COUNTDOWN");
  const aiBanner = getRoleSection(sections, "aiBanner");
  const product = getByType(sections, "PRODUCT");
  const socialProof = getRoleSection(sections, "socialProof");
  ensureAiRecommendationUpsellCatalogCtaButtons(sections);
  normalizeAiRecommendationUpsellSections(sections, {
    buttonText: options.buttonText,
  });

  const buttons = sections.filter((s) => s?.type === "BUTTON");
  const primaryCta =
    buttons.find((s) => s?.setting?.role === "primaryCta") || buttons[0];
  const secondaryCta =
    buttons.find((s) => s?.setting?.role === "secondaryCta") || buttons[1];
  const trust = getRoleSection(sections, "trust");
  const footer =
    sections.find((s) => s?.type === "BRAND_ATTRIBUTION") ||
    getRoleSection(sections, "brandAttribution") ||
    getRoleSection(sections, "footer");

  const shellDesktop = step0?.popupSettings?.customCSS?.desktop || {};
  const shellMobile = step0?.popupSettings?.customCSS?.mobile || {};
  step0.popupSettings = step0.popupSettings || {};
  step0.popupSettings.customCSS = step0.popupSettings.customCSS || {};
  step0.popupSettings.customCSS.desktop = {
    ...shellDesktop,
    width:
      shellDesktop.width && shellDesktop.width !== 480
        ? shellDesktop.width
        : SHELL_DESKTOP_WIDTH,
    ...(shellDesktop.borderRadius == null
      ? { borderRadius: options.borderRadius }
      : {}),
  };
  step0.popupSettings.customCSS.mobile = {
    ...shellMobile,
    width: shellMobile.width === 320 ? "100%" : shellMobile.width || "100%",
    widthUnit: "%",
    ...(shellMobile.borderRadius == null
      ? { borderRadius: options.borderRadius }
      : {}),
  };

  let countdownDisplay = null;
  if (countdown) {
    markLayoutFlow(countdown);
    countdownDisplay = syncCountdownSectionDisplay(countdown, popup, options);
    if (countdownDisplay?.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR) {
      placeLayout(
        countdown,
        roleBoxes.desktop.countdown,
        roleBoxes.mobile.countdown
      );
    } else {
      placeLayout(
        countdown,
        roleBoxes.desktop.countdown,
        roleBoxes.mobile.countdown
      );
    }
  }
  if (aiBanner) {
    markLayoutFlow(aiBanner);
    setVisibility(aiBanner, options.showAiSummaryRibbon);
    applyAiSummaryVariant(aiBanner, options.designVariants.aiSummaryRibbon);
    ensureAiBannerSampleCopy(aiBanner);
    placeLayout(
      aiBanner,
      roleBoxes.desktop.aiBanner,
      roleBoxes.mobile.aiBanner
    );
  }
  if (product) {
    markLayoutFlow(product);
    ensureProductFallback(product);
    applyProductVariant(product, options.designVariants.productCard);
    placeLayout(
      product,
      roleBoxes.desktop.product,
      roleBoxes.mobile.product
    );
    if (Array.isArray(product.sections)) {
      for (const sub of product.sections) {
        const d = PRODUCT_SUB_LAYOUT.desktop[sub.type];
        const m = PRODUCT_SUB_LAYOUT.mobile[sub.type];
        if (sub.type === "VARIANT_PICKER") {
          setVisibility(sub, options.showVariantPicker);
          applyVariantPickerVariant(sub, options.designVariants.variantPicker);
        }
        if (sub.type === "QUANTITY_PICKER") {
          setVisibility(sub, options.showQuantityAndVolume);
          sub.setting.customCSS = sub.setting.customCSS || {};
          const qtyDesktop = sub.setting.customCSS.desktop || {};
          const qtyMobile = sub.setting.customCSS.mobile || {};
          sub.setting.customCSS.desktop = {
            ...qtyDesktop,
            ...(qtyDesktop.borderRadius == null
              ? { borderRadius: options.borderRadius }
              : {}),
          };
          sub.setting.customCSS.mobile = {
            ...qtyMobile,
            ...(qtyMobile.borderRadius == null
              ? { borderRadius: options.borderRadius }
              : {}),
          };
        }
        markLayoutFlow(sub);
        if (d && m) placeLayout(sub, d, m);
      }
    }
  }
  if (socialProof) {
    markLayoutFlow(socialProof);
    setVisibility(socialProof, options.showSocialProof);
    applySocialProofVariant(socialProof, options.designVariants.socialProof);
    placeLayout(
      socialProof,
      roleBoxes.desktop.socialProof,
      roleBoxes.mobile.socialProof
    );
  }
  if (primaryCta) {
    markLayoutFlow(primaryCta);
    primaryCta.text = resolveSectionText(primaryCta, options.buttonText);
    primaryCta.setting = primaryCta.setting || {};
    primaryCta.setting.btn_type = primaryCta.setting.btn_type || { type: "text" };
    applyCtaVariant(primaryCta, options.designVariants.ctaButtons, true, themeTokens);
    placeLayout(
      primaryCta,
      roleBoxes.desktop.primaryCta,
      roleBoxes.mobile.primaryCta
    );
  }
  if (secondaryCta) {
    markLayoutFlow(secondaryCta);
    secondaryCta.text = resolveSectionText(secondaryCta, options.declineText);
    secondaryCta.setting = secondaryCta.setting || {};
    secondaryCta.setting.btn_type = secondaryCta.setting.btn_type || { type: "text" };
    applyCtaVariant(secondaryCta, options.designVariants.ctaButtons, false, themeTokens);
    placeLayout(
      secondaryCta,
      roleBoxes.desktop.secondaryCta,
      roleBoxes.mobile.secondaryCta
    );
  }
  if (trust) {
    markLayoutFlow(trust);
    setVisibility(trust, options.showTrustBadges);
    applyTrustVariant(trust, options.designVariants.trustBadges);
    placeLayout(trust, roleBoxes.desktop.trust, roleBoxes.mobile.trust);
  }
  if (footer) {
    markLayoutFlow(footer);
    placeLayout(footer, roleBoxes.desktop.footer, roleBoxes.mobile.footer);
  }

  popup.generalSettings = {
    ...gs,
    popupLayoutId: AI_RECOMMENDATION_UPSELL_LAYOUT_ID,
    layoutPreset: AI_RECOMMENDATION_UPSELL_LAYOUT_ID,
  };

  return popup;
};

export default ensureAiRecommendationUpsellLayoutCompatibility;

/** @deprecated Use ensureAiRecommendationUpsellLayoutCompatibility */
export const ensureAiConversionLayoutCompatibility =
  ensureAiRecommendationUpsellLayoutCompatibility;
